import React, { FC, useRef } from 'react';
import { useOwnerGroupAsssets } from '~/hooks/assets/useAssetManagement';
import CobuTableAdvanced from '../CobuTableAdvanced/CobuTableAdvanced';
import {
  AssetAccessors,
  IOwnerGroupAsset,
  IOwnerGroupProperty
} from '~/types/assets';
import './AssetMangementTable.css';
import Spinner from '../Spinner/Spinner';

interface IProps {
  showSearchHeader?: boolean;
  ownerId?: string;
  heading: string;
  isFromOwnerGroup?: boolean;
}

const PLATFORM_OPTIONS = [
  { key: 'cobu', label: 'Cobu Buildings' },
  { key: 'integrated', label: 'Integrated Buildings' },
  { key: 'entrata_homebody', label: 'Entrata/Homebody Buildings' }
];

const BILLING_OPTIONS = [
  { key: 'free', label: 'Free Buildings' },
  { key: 'paid', label: 'Paid Buildings' }
];

export const AssetManagementTable: FC<IProps> = ({
  showSearchHeader = false,
  ownerId,
  isFromOwnerGroup,
  heading
}) => {
  const {
    ownerGroupWiseAssets,
    onPlatformToggle,
    onBillingToggle,
    assetManagementTableConfig,
    loading,
    selectedOwnerGroupProperties,
    selectedPlatforms,
    selectedBillings,
    isVCMorSuperAdmin
  } = useOwnerGroupAsssets(ownerId, isFromOwnerGroup);

  const inputRef = useRef<{ clearSearchInput: () => void }>(null);

  if (loading) {
    return <Spinner />;
  }

  const clearSearchInput = () => {
    if (inputRef?.current?.clearSearchInput) {
      inputRef.current.clearSearchInput();
    }
  };

  const columnData = ownerId
    ? (selectedOwnerGroupProperties as IOwnerGroupProperty[])
    : (ownerGroupWiseAssets as IOwnerGroupAsset[]);

  const showHeader = showSearchHeader && isVCMorSuperAdmin;

  return (
    <>
      <div className='tableHeader'>
        <div className='title bold tableTitle'>{heading}</div>
      </div>
      {showHeader && (
        <div className='filter-panel'>
          <div className='filter-row'>
            <span className='filter-row-label'>PLATFORM</span>
            <div className='filter-row-buttons'>
              {PLATFORM_OPTIONS.map(({ key, label }) => (
                <button
                  key={key}
                  className={`btn-standard btn-secondary ${
                    selectedPlatforms.has(key) ? 'switchSelected' : 'defaultSwitch'
                  }`}
                  onClick={() => {
                    onPlatformToggle(key);
                    clearSearchInput();
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className='filter-row'>
            <span className='filter-row-label'>BILLING</span>
            <div className='filter-row-buttons'>
              {BILLING_OPTIONS.map(({ key, label }) => (
                <button
                  key={key}
                  className={`btn-standard btn-secondary ${
                    selectedBillings.has(key) ? 'switchSelected' : 'defaultSwitch'
                  }`}
                  onClick={() => {
                    onBillingToggle(key);
                    clearSearchInput();
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {!!columnData && (
        <CobuTableAdvanced
          {...(isVCMorSuperAdmin
            ? { containerStyle: { position: 'relative' } }
            : {})}
          actionsStyle={
            isVCMorSuperAdmin
              ? {
                  top: '-80px',
                  right: '0px'
                }
              : {
                  position: 'static',
                  justifyContent: 'flex-end',
                  marginBottom: '10px'
                }
          }
          columns={[...assetManagementTableConfig]}
          data={columnData}
          noData={{
            label: 'No Data',
            action: undefined,
            image: undefined
          }}
          {...(showSearchHeader
            ? {
                search: {
                  searchField: AssetAccessors.buildingName,
                  placeholder: 'Search by property'
                }
              }
            : {})}
          showPagination
          cellTextAlign='center'
          ref={inputRef}
        />
      )}
    </>
  );
};
